import axios from 'axios';

const API = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:8080/api',
  headers: { 'Content-Type': 'application/json' },
});

// Axios interceptor untuk attach JWT token dari localStorage
API.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Axios interceptor untuk handle 401 redirect
API.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      // Only redirect if not already on login page
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

export const getDashboard = () => API.get('/dashboard');
export const getSystemHealth = () => API.get('/system/health');
export const getSystemHealthHistory = (hours = 24) => API.get('/system/health/history', { params: { hours } });
export const getVehicles = (params) => API.get('/vehicles', { params });
export const getCMVehicles = () => API.get('/vehicles', { params: { all: true, filter_is_active: true } });

export const getVehicle = (id) => API.get(`/vehicles/${id}`);
export const createVehicle = (data) => API.post('/vehicles', data);
export const updateVehicle = (id, data) => API.put(`/vehicles/${id}`, data);
export const deleteVehicle = (id) => API.delete(`/vehicles/${id}`);
export const getKlien = () => API.get('/klien');
export const upsertKlien = (data) => API.post('/klien', data);
export const getRegions = () => API.get('/regions');
export const getRegion = (id) => API.get(`/regions/${id}`);
export const createRegion = (data) => API.post('/regions', data);
export const updateRegion = (id, data) => API.put(`/regions/${id}`, data);
export const deleteRegion = (id) => API.delete(`/regions/${id}`);
export const getSpareparts = () => API.get('/spareparts');
export const getSparepart = (id) => API.get(`/spareparts/${id}`);
export const createSparepart = (data) => API.post('/spareparts', data);
export const updateSparepart = (id, data) => API.put(`/spareparts/${id}`, data);
export const deleteSparepart = (id) => API.delete(`/spareparts/${id}`);
export const login = (username, password) => API.post('/auth/login', { username, password });
export const getMe = () => API.get('/auth/me');

// User Management API
export const getUsers = () => API.get('/users');
export const getUser = (id) => API.get(`/users/${id}`);
export const createUser = (data) => API.post('/users', data);
export const updateUser = (id, data) => API.put(`/users/${id}`, data);
export const deleteUser = (id) => API.delete(`/users/${id}`);
export const uploadUserPhoto = (file, userId, onProgress) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('user_id', userId);
  return API.post('/users/upload-photo', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    onUploadProgress: onProgress,
  });
};

// Bagian API
export const getBagian = () => API.get('/bagian');
export const getBagianItem = (id) => API.get(`/bagian/${id}`);
export const createBagian = (data) => API.post('/bagian', data);
export const updateBagian = (id, data) => API.put(`/bagian/${id}`, data);
export const deleteBagian = (id) => API.delete(`/bagian/${id}`);

// Menu Permissions API
export const getMenuPermissions = (role) => API.get('/menu-permissions', { params: { role } });
export const updateMenuPermissions = (permissions) => API.post('/menu-permissions', { permissions });

// User's own menu permissions (for sidebar filtering)
export const getUserMenus = () => API.get('/auth/menus');

export const downloadVehicleTemplate = () => API.get('/vehicles/template', { responseType: 'blob' });

export const getVehicleFilterOptions = () => API.get('/vehicles/filter-options');

// Kontrol manual blokir input CM/PM (klien BDL) — panel di menu Kendaraan
export const getKlienHoldSetting = () => API.get('/klien-hold/setting');
export const updateKlienHoldSetting = (data) => API.put('/klien-hold/setting', data);

export const renameMasterData = (field, oldValue, newValue) => API.put('/vehicles/master-data', { field, old_value: oldValue, new_value: newValue });
export const deleteMasterData = (field, value) => API.delete('/vehicles/master-data', { data: { field, value } });

export const deleteAllVehicles = () => API.delete('/vehicles/delete-all');

export const importVehicles = (file, onProgress) => {
  const formData = new FormData();
  formData.append('file', file);

  return API.post('/vehicles/import', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    onUploadProgress: onProgress,
  });
};

export const getSparepartStats = () => API.get('/spareparts/stats');

// ========================================
// Karyawan API
// ========================================
export const getKaryawan = () => API.get('/karyawan');
export const getKaryawanById = (id) => API.get(`/karyawan/${id}`);
export const createKaryawan = (data) => API.post('/karyawan', data);
export const updateKaryawan = (id, data) => API.put(`/karyawan/${id}`, data);
export const deleteKaryawan = (id) => API.delete(`/karyawan/${id}`);

// ========================================
// Penugasan API
// ========================================
export const getPenugasan = () => API.get('/penugasan');
export const getPenugasanById = (id) => API.get(`/penugasan/${id}`);
export const createPenugasan = (data) => API.post('/penugasan', data);
export const updatePenugasan = (id, data) => API.put(`/penugasan/${id}`, data);
export const deletePenugasan = (id) => API.delete(`/penugasan/${id}`);
export const getPenugasanRollover = (params) => API.get('/penugasan/rollover', { params });
export const getPenugasanCopyPrevious = (params) => API.get('/penugasan/copy-previous', { params });

// ========================================
// PM (Preventive Maintenance) API
// ========================================
export const getPM = (params) => API.get('/pm', { params });
export const getPMById = (id) => API.get(`/pm/${id}`);
// Data PM lengkap (tanpa kolom berat) khusus export — tidak dibatasi 500 baris
// seperti /pm, agar export periode lama tidak terpotong.
export const getPMExport = (params) => API.get('/pm/export', { params });
export const createPM = (data) => API.post('/pm', data);
export const updatePM = (id, data) => API.put(`/pm/${id}`, data);
export const deletePM = (id) => API.delete(`/pm/${id}`);
export const uploadPMFile = (file, fileType, onProgress) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('type', fileType);
  return API.post('/pm/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    onUploadProgress: onProgress,
  });
};

// ========================================
// CM (Corrective Maintenance) API
// ========================================
export const getCM = (params) => API.get('/cm', { params });
export const getCMById = (id) => API.get(`/cm/${id}`);
export const createCM = (data) => API.post('/cm', data);
export const updateCM = (id, data) => API.put(`/cm/${id}`, data);
export const deleteCM = (id) => API.delete(`/cm/${id}`);
export const updateCMProgress = (id, data) => API.put(`/cm/${id}/progress`, data);
export const selesaikanCM = (id, data) => API.put(`/cm/${id}/selesaikan`, data);
export const verifikasiCM = (id, data) => API.put(`/cm/${id}/verifikasi`, data);
export const approveCM = (id, data) => API.put(`/cm/${id}/approve`, data);
export const getCMLogs = (id) => API.get(`/cm/${id}/logs`);
export const getCMAllLogs = (params) => API.get('/cm/logs', { params });
export const getCMDashboard = (params) => API.get('/cm/dashboard', { params });
export const getPMDashboard = (params) => API.get('/pm/dashboard', { params });
export const uploadCMFile = (file, fileType, onProgress) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('type', fileType);
  return API.post('/cm/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    onUploadProgress: onProgress,
  });
};

// ========================================
// Absensi (Kehadiran) API
// ========================================
export const getAbsensi = (params) => API.get('/absensi', { params });
export const getAbsensiById = (id) => API.get(`/absensi/${id}`);
export const createAbsensi = (data) => API.post('/absensi', data);
export const updateAbsensi = (id, data) => API.put(`/absensi/${id}`, data);
export const deleteAbsensi = (id) => API.delete(`/absensi/${id}`);
export const absensiCheckin = (data) => API.post('/absensi/checkin', data);
export const absensiCheckout = (data) => API.post('/absensi/checkout', data);
export const getAbsensiStats = () => API.get('/absensi/stats');
export const getAbsensiRekap = (params) => API.get('/absensi/rekap', { params });
export const getAbsensiRekapOrang = (params) => API.get('/absensi/rekap-orang', { params });

// ========================================
// Jadwal Kerja API
// ========================================
export const getJadwalKerja = (params) => API.get('/jadwal-kerja', { params });
export const getJadwalKerjaById = (id) => API.get(`/jadwal-kerja/${id}`);
export const createJadwalKerja = (data) => API.post('/jadwal-kerja', data);
export const updateJadwalKerja = (id, data) => API.put(`/jadwal-kerja/${id}`, data);
export const deleteJadwalKerja = (id) => API.delete(`/jadwal-kerja/${id}`);
export const createJadwalBulk = (data) => API.post('/jadwal-kerja/bulk', data);

// ========================================
// Pengajuan Lembur API
// ========================================
export const getPengajuanLembur = (params) => API.get('/pengajuan-lembur', { params });
export const getPengajuanLemburById = (id) => API.get(`/pengajuan-lembur/${id}`);
export const createPengajuanLembur = (data) => API.post('/pengajuan-lembur', data);
export const updatePengajuanLembur = (id, data) => API.put(`/pengajuan-lembur/${id}`, data);
export const deletePengajuanLembur = (id) => API.delete(`/pengajuan-lembur/${id}`);
export const approvePengajuanLembur = (id, data) => API.put(`/pengajuan-lembur/approve/${id}`, data);
export const getLemburStats = (params) => API.get('/pengajuan-lembur/stats', { params });
export const getPengaturanLembur = () => API.get('/pengajuan-lembur/settings');
export const updatePengaturanLembur = (data) => API.put('/pengajuan-lembur/settings', data);

// ========================================
// Checklist Harian API
// ========================================
export const getChecklists = (params) => API.get('/checklist', { params });
export const getChecklistById = (id) => API.get(`/checklist/${id}`);
export const createChecklist = (data) => API.post('/checklist', data);
export const updateChecklist = (id, data) => API.put(`/checklist/${id}`, data);
export const deleteChecklist = (id) => API.delete(`/checklist/${id}`);// ========================================
// Tagihan (Invoice) API
// ========================================
export const getTagihan = (params) => API.get('/tagihan', { params });
export const getTagihanById = (id) => API.get(`/tagihan/${id}`);
export const createTagihan = (data) => API.post('/tagihan', data);
export const updateTagihan = (id, data) => API.put(`/tagihan/${id}`, data);
export const deleteTagihan = (id) => API.delete(`/tagihan/${id}`);
export const generateTagihanFromCM = (data) => API.post('/tagihan/generate', data);
export const getTagihanStats = () => API.get('/tagihan/stats');

// ========================================
// Akuntansi & Keuangan API
// ========================================
export const getCOA = () => API.get('/akuntansi/coa');
export const getCOAById = (id) => API.get(`/akuntansi/coa/${id}`);
export const createCOA = (data) => API.post('/akuntansi/coa', data);
export const updateCOA = (id, data) => API.put(`/akuntansi/coa/${id}`, data);
export const deleteCOA = (id) => API.delete(`/akuntansi/coa/${id}`);
export const getJurnal = (params) => API.get('/akuntansi/jurnal', { params });
export const getJurnalById = (id) => API.get(`/akuntansi/jurnal/${id}`);
export const createJurnal = (data) => API.post('/akuntansi/jurnal', data);
export const deleteJurnal = (id) => API.delete(`/akuntansi/jurnal/${id}`);
export const createJurnalDariTagihan = (data) => API.post('/akuntansi/jurnal/dari-tagihan', data);
export const getKandidatJurnalOtomatis = (params) => API.get('/akuntansi/jurnal/otomatis', { params });
export const createJurnalDariPembelian = (data) => API.post('/akuntansi/jurnal/dari-pembelian', data);
export const createJurnalDariPenerimaan = (data) => API.post('/akuntansi/jurnal/dari-penerimaan', data);
export const createJurnalDariGaji = (data) => API.post('/akuntansi/jurnal/dari-gaji', data);
export const getNeraca = (params) => API.get('/akuntansi/neraca', { params });
export const getLabaRugi = (params) => API.get('/akuntansi/laba-rugi', { params });
export const getBukuBesar = (params) => API.get('/akuntansi/buku-besar', { params });
export const getAkuntansiDashboard = () => API.get('/akuntansi/dashboard');
export const getSaldoAwal = () => API.get('/akuntansi/saldo-awal');
export const saveSaldoAwal = (data) => API.post('/akuntansi/saldo-awal', data);

// ========================================
// Laporan (Reports) API
// ========================================
export const getLaporan = (params) => API.get('/laporan', { params });
export const getLaporanPenggunaanSparepart = (params) => API.get('/laporan/penggunaan-sparepart', { params });
// Daftar armada untuk tab Laporan → Kendaraan (filter multi Nama Klien & No Kontrak)
export const getLaporanKendaraan = (params) => API.get('/laporan/kendaraan', { params });
export const getLaporanAnalisaCm = (params) => API.get('/laporan/analisa-cm', { params });
// Perjalanan satu armada: riwayat PM + CM, sparepart, jasa, teknisi, biaya
export const getLaporanKendaraanDetail = (params) => API.get('/laporan/kendaraan-detail', { params });
// Foto bukti satu aktivitas (PM/CM) — dikirim sebagai gambar, bukan base64 di JSON
export const getLaporanKendaraanFoto = (params) => API.get('/laporan/kendaraan-foto', { params, responseType: 'blob' });
export const getLaporanKlien = (params) => API.get('/laporan-klien', { params });
export const getClientList = () => API.get('/clients');

// ========================================
// Jasa Layanan API
// ========================================
export const getJasaLayanan = () => API.get('/jasa-layanan');
export const getJasaLayananById = (id) => API.get(`/jasa-layanan/${id}`);
export const createJasaLayanan = (data) => API.post('/jasa-layanan', data);
export const updateJasaLayanan = (id, data) => API.put(`/jasa-layanan/${id}`, data);
export const deleteJasaLayanan = (id) => API.delete(`/jasa-layanan/${id}`);
export const importJasaLayanan = (file, onProgress) => {
  const formData = new FormData();
  formData.append('file', file);
  return API.post('/jasa-layanan/import', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    onUploadProgress: onProgress,
  });
};

// ========================================
// Notifications API
// ========================================
export const getNotifications = (limit) => API.get('/notifications', { params: { limit } });
// ========================================
// Activity API
// ========================================
export const getActivities = (params) => API.get('/activity', { params });
export const getActivity = (id) => API.get(`/activity/${id}`);
export const createActivity = (data) => API.post('/activity', data);
export const updateActivity = (id, data) => API.put(`/activity/${id}`, data);
export const deleteActivity = (id) => API.delete(`/activity/${id}`);
export const uploadActivityFile = (file, onProgress) => {
  const formData = new FormData();
  formData.append('file', file);
  return API.post('/activity/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    onUploadProgress: onProgress,
  });
};
export const getMyActivities = () => API.get('/activity/my');
export const getActivityLaporan = () => API.get('/activity/laporan');
export const getActivityNotifCount = () => API.get('/activity/unread-count');

// Marketing Activity API
export const getMarketingActivities = (params) => API.get('/marketing-activity', { params });
export const getMarketingActivity = (id) => API.get(`/marketing-activity/${id}`);
export const createMarketingActivity = (data) => API.post('/marketing-activity', data);
export const updateMarketingActivity = (id, data) => API.put(`/marketing-activity/${id}`, data);
export const deleteMarketingActivity = (id) => API.delete(`/marketing-activity/${id}`);
export const getMarketingStats = () => API.get('/marketing-activity/stats');
export const  getMarketingLogs = (id) => API.get(`/marketing-activity/logs/${id}`);
  export const createMarketingLog = (data) => API.post('/marketing-activity/logs', data);

  // ============ MARKETING CLIENTS (CRM) ============
  export const getMarketingClients = (params) => API.get('/marketing-clients', { params });
  export const getMarketingClient = (id) => API.get(`/marketing-clients/${id}`);
  export const createMarketingClient = (data) => API.post('/marketing-clients', data);
  export const updateMarketingClient = (id, data) => API.put(`/marketing-clients/${id}`, data);
  export const deleteMarketingClient = (id) => API.delete(`/marketing-clients/${id}`);
  export const getMarketingClientStats = () => API.get('/marketing-clients/stats');

export const getUnreadCount = () => API.get('/notifications/unread-count');
export const markNotifRead = (id) => API.post('/notifications/mark-read', { id });
export const markAllNotifRead = () => API.post('/notifications/mark-all-read');

export const downloadSparepartTemplate = () => API.get('/spareparts/template', { responseType: 'blob' });

export const deleteAllSpareparts = () => API.delete('/spareparts/delete-all');

export const importSpareparts = (file, onProgress) => {
  const formData = new FormData();
  formData.append('file', file);

  return API.post('/spareparts/import', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    onUploadProgress: onProgress,
  });
};

// ========================================
// Settings API
// ========================================
export const getSettings = () => API.get('/settings');
export const updateSettings = (data) => API.put('/settings', data);
// Public settings (any authenticated user) — safe subset for printing defaults
export const getPublicSettings = () => API.get('/settings/public');
// Update TTD & company stamp defaults — any authenticated user (not admin-only)
export const updateTtdStempel = (data) => API.put('/settings/ttd-stempel', data);

// ========================================
// Inventori API
// ========================================
export const getGudang = () => API.get('/inventori/gudang');
export const createGudang = (data) => API.post('/inventori/gudang', data);
export const updateGudang = (id, data) => API.put(`/inventori/gudang/${id}`, data);
export const deleteGudang = (id) => API.delete(`/inventori/gudang/${id}`);

export const getInventoriSpareparts = (params) => API.get('/inventori/spareparts', { params });
export const getInventoriSparepart = (id) => API.get(`/inventori/spareparts/${id}`);
export const createInventoriSparepart = (data) => API.post('/inventori/spareparts', data);
export const updateInventoriSparepart = (id, data) => API.put(`/inventori/spareparts/${id}`, data);
export const deleteInventoriSparepart = (id) => API.delete(`/inventori/spareparts/${id}`);
export const downloadInventoriSparepartTemplate = () => API.get('/inventori/spareparts/template', { responseType: 'blob' });
export const deleteAllInventoriSpareparts = () => API.delete('/inventori/spareparts/delete-all');
export const importInventoriSpareparts = (file, onProgress) => {
  const formData = new FormData();
  formData.append('file', file);
  return API.post('/inventori/spareparts/import', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    onUploadProgress: onProgress,
  });
};

export const getPembelian = () => API.get('/inventori/pembelian');
export const getVerifikasiCount = () => API.get('/inventori/pembelian/verifikasi-count');
export const getPembelianVerifikasiLog = () => API.get('/inventori/pembelian/verifikasi-log');
export const getVerifikasiHariIniCount = () => API.get('/inventori/pembelian/verifikasi-hari-ini-count');
export const getPembelianById = (id) => API.get(`/inventori/pembelian/${id}`);
export const createPembelian = (data) => API.post('/inventori/pembelian', data);
export const updatePembelian = (id, data) => API.put(`/inventori/pembelian/${id}`, data);
export const deletePembelian = (id) => API.delete(`/inventori/pembelian/${id}`);
export const verifikasiPembelian = (id, action) => API.put(`/inventori/pembelian/${id}/verifikasi`, { action });

export const getReturPembelian = () => API.get('/inventori/retur-pembelian');
export const getReturPembelianById = (id) => API.get(`/inventori/retur-pembelian/${id}`);
export const createReturPembelian = (data) => API.post('/inventori/retur-pembelian', data);
export const updateReturPembelian = (id, data) => API.put(`/inventori/retur-pembelian/${id}`, data);
export const deleteReturPembelian = (id) => API.delete(`/inventori/retur-pembelian/${id}`);
export const verifikasiReturPembelian = (id, action) => API.put(`/inventori/retur-pembelian/${id}/verifikasi`, { action });
export const getVerifikasiReturCount = () => API.get('/inventori/retur-pembelian/verifikasi-count');
export const getReturVerifikasiLog = () => API.get('/inventori/retur-pembelian/verifikasi-log');
export const getReturVerifikasiHariIniCount = () => API.get('/inventori/retur-pembelian/verifikasi-hari-ini-count');

export const getStokOpname = () => API.get('/inventori/stok-opname');
export const getStokOpnameById = (id) => API.get(`/inventori/stok-opname/${id}`);
export const createStokOpname = (data) => API.post('/inventori/stok-opname', data);
export const deleteStokOpname = (id) => API.delete(`/inventori/stok-opname/${id}`);

export const getPenyesuaianStok = () => API.get('/inventori/penyesuaian-stok');
export const createPenyesuaianStok = (data) => API.post('/inventori/penyesuaian-stok', data);
export const deletePenyesuaianStok = (id) => API.delete(`/inventori/penyesuaian-stok/${id}`);

export const getStokKeluar = () => API.get('/inventori/stok-keluar');
export const getStokKeluarById = (id) => API.get(`/inventori/stok-keluar/${id}`);
export const createStokKeluar = (data) => API.post('/inventori/stok-keluar', data);
export const deleteStokKeluar = (id) => API.delete(`/inventori/stok-keluar/${id}`);


export const getKategoriSparepart = () => API.get('/inventori/kategori-sparepart');
export const createKategori = (nama) => API.post('/inventori/kategori-sparepart', { nama });
export const renameKategori = (kategoriLama, kategoriBaru) => API.put('/inventori/kategori-sparepart', { kategori_lama: kategoriLama, kategori_baru: kategoriBaru });
export const deleteKategori = (kategori) => API.delete('/inventori/kategori-sparepart', { params: { kategori } });
export const getInventoriDashboard = () => API.get('/inventori/dashboard');
export const getSupplier = () => API.get('/inventori/supplier');
export const createSupplier = (data) => API.post('/inventori/supplier', data);
export const updateSupplier = (id, data) => API.put(`/inventori/supplier/${id}`, data);
export const deleteSupplier = (id) => API.delete(`/inventori/supplier/${id}`);

export const getSupplierDebt = () => API.get('/inventori/supplier-debt');
export const getPurchaseOrders = () => API.get('/inventori/purchase-orders');
export const getPurchaseOrderById = (id) => API.get(`/inventori/purchase-orders/${id}`);
export const createPurchaseOrder = (data) => API.post('/inventori/purchase-orders', data);
export const updatePurchaseOrder = (id, data) => API.put(`/inventori/purchase-orders/${id}`, data);
export const deletePurchaseOrder = (id) => API.delete(`/inventori/purchase-orders/${id}`);
export const updatePurchaseOrderStatus = (id, status) => API.put(`/inventori/purchase-orders/${id}/status`, { status });
export const verifyPurchaseOrderItems = (id, payload) => API.put(`/inventori/purchase-orders/${id}/verifikasi-item`, payload);

export const getPenerimaanPembelian = () => API.get('/inventori/penerimaan-pembelian');
export const getPenerimaanPembelianById = (id) => API.get(`/inventori/penerimaan-pembelian/${id}`);
export const createPenerimaanPembelian = (data) => API.post('/inventori/penerimaan-pembelian', data);
export const updatePenerimaanPembelian = (id, data) => API.put(`/inventori/penerimaan-pembelian/${id}`, data);
export const deletePenerimaanPembelian = (id) => API.delete(`/inventori/penerimaan-pembelian/${id}`);
export const setMetodePembayaranPenerimaan = (id, metode_pembayaran) => API.put(`/inventori/penerimaan-pembelian/${id}/pembayaran`, { metode_pembayaran });

export const getFakturPembelian = () => API.get('/inventori/faktur-pembelian');
export const getFakturPembelianById = (id) => API.get(`/inventori/faktur-pembelian/${id}`);
export const createFakturPembelian = (data) => API.post('/inventori/faktur-pembelian', data);
export const updateFakturPembelian = (id, data) => API.put(`/inventori/faktur-pembelian/${id}`, data);
export const deleteFakturPembelian = (id) => API.delete(`/inventori/faktur-pembelian/${id}`);

export const getPembayaranHutang = () => API.get('/inventori/pembayaran-hutang');
// supplier_name opsional — tanpa argumen: daftar semua nota hutang belum lunas (semua supplier)
export const getHutangNota = (supplier_name, petugas) => API.get('/inventori/pembayaran-hutang/nota', {
  params: {
    ...(supplier_name ? { supplier_name } : {}),
    ...(petugas ? { petugas } : {}),
  },
});

// Rekap utang reimbursement karyawan (uang pribadi yang ditalangi petugas/teknisi)
export const getReimbursementDebt = () => API.get('/inventori/reimbursement-debt');
export const getPembayaranHutangById = (id) => API.get(`/inventori/pembayaran-hutang/${id}`);
export const createPembayaranHutang = (data) => API.post('/inventori/pembayaran-hutang', data);
export const deletePembayaranHutang = (id) => API.delete(`/inventori/pembayaran-hutang/${id}`);

// ========================================
// Catatan (Notes) API
// ========================================
export const getCatatan = () => API.get('/catatan');
export const getCatatanById = (id) => API.get(`/catatan/${id}`);
export const createCatatan = (data) => API.post('/catatan', data);
export const updateCatatan = (id, data) => API.put(`/catatan/${id}`, data);
export const deleteCatatan = (id) => API.delete(`/catatan/${id}`);

// ========================================
// Master Role API
// ========================================
export const getKPITeknisi = (params) => API.get('/kpi-teknisi', { params });
export const getKPIAkhir = (params) => API.get('/kpi-teknisi/akhir', { params });

export const getRoles = () => API.get('/roles');
export const getRoleById = (id) => API.get(`/roles/${id}`);
export const createRole = (data) => API.post('/roles', data);
export const updateRole = (id, data) => API.put(`/roles/${id}`, data);
export const deleteRole = (id) => API.delete(`/roles/${id}`);

// ========================================
// HRIS API (Human Resource Information System)
// ========================================

// Kontrak Karyawan
export const getHrisKontrak = (params) => API.get('/hris/kontrak', { params });
export const getHrisKontrakById = (id) => API.get(`/hris/kontrak/${id}`);
export const createHrisKontrak = (data) => API.post('/hris/kontrak', data);
export const updateHrisKontrak = (id, data) => API.put(`/hris/kontrak/${id}`, data);
export const deleteHrisKontrak = (id) => API.delete(`/hris/kontrak/${id}`);

// Cuti & Izin
export const getHrisCuti = (params) => API.get('/hris/cuti', { params });
export const getHrisCutiById = (id) => API.get(`/hris/cuti/${id}`);
export const createHrisCuti = (data) => API.post('/hris/cuti', data);
export const updateHrisCuti = (id, data) => API.put(`/hris/cuti/${id}`, data);
export const deleteHrisCuti = (id) => API.delete(`/hris/cuti/${id}`);
export const approveHrisCuti = (id, data) => API.put(`/hris/cuti/approve/${id}`, data);
export const getHrisSisaCuti = (params) => API.get('/hris/sisa-cuti', { params });

// Pelanggaran & Sanksi
export const getHrisPelanggaran = (params) => API.get('/hris/pelanggaran', { params });
export const getHrisPelanggaranById = (id) => API.get(`/hris/pelanggaran/${id}`);
export const createHrisPelanggaran = (data) => API.post('/hris/pelanggaran', data);
export const updateHrisPelanggaran = (id, data) => API.put(`/hris/pelanggaran/${id}`, data);
export const deleteHrisPelanggaran = (id) => API.delete(`/hris/pelanggaran/${id}`);

// Komponen Gaji
export const getHrisGaji = (params) => API.get('/hris/gaji', { params });
export const getHrisGajiById = (id) => API.get(`/hris/gaji/${id}`);
export const createHrisGaji = (data) => API.post('/hris/gaji', data);
export const batchHrisGaji = (data) => API.post('/hris/gaji/batch', data);
export const updateHrisGaji = (id, data) => API.put(`/hris/gaji/${id}`, data);
export const deleteHrisGaji = (id) => API.delete(`/hris/gaji/${id}`);

// Penggajian per periode (alur baru: pengajuan → ACC → bayar sebagian → laporan)
export const getHrisPenggajian = (params) => API.get('/hris/penggajian', { params });
export const getHrisPenggajianById = (id) => API.get(`/hris/penggajian/${id}`);
export const createHrisPenggajian = (data) => API.post('/hris/penggajian', data);
export const batchHrisPenggajian = (data) => API.post('/hris/penggajian/batch', data);
export const updateHrisPenggajian = (id, data) => API.put(`/hris/penggajian/${id}`, data);
export const deleteHrisPenggajian = (id) => API.delete(`/hris/penggajian/${id}`);
export const approveHrisPenggajian = (id, data) => API.post(`/hris/penggajian/${id}/approve`, data || {});
export const rejectHrisPenggajian = (id, data) => API.post(`/hris/penggajian/${id}/reject`, data || {});
export const bayarHrisPenggajian = (id, data) => API.post(`/hris/penggajian/${id}/bayar`, data);

// Slip Gaji (Payroll) — halaman lama, tetap tersedia
export const getHrisSlipGaji = (params) => API.get('/hris/slip-gaji', { params });
export const getHrisSlipGajiById = (id) => API.get(`/hris/slip-gaji/${id}`);
export const deleteHrisSlipGaji = (id) => API.delete(`/hris/slip-gaji/${id}`);
export const generateHrisSlipGaji = (data) => API.post('/hris/slip-gaji/generate', data);
export const generateHrisSlipGajiBulk = (data) => API.post('/hris/slip-gaji/generate-bulk', data);
export const publishHrisSlipGaji = (id, data) => API.put(`/hris/slip-gaji/publish/${id}`, data);
export const publishBulkHrisSlipGaji = (data) => API.put('/hris/slip-gaji/publish-bulk', data);

// KPI / Penilaian Kinerja
export const getHrisKPI = (params) => API.get('/hris/kpi', { params });
export const getHrisKPIById = (id) => API.get(`/hris/kpi/${id}`);
export const createHrisKPI = (data) => API.post('/hris/kpi', data);
export const updateHrisKPI = (id, data) => API.put(`/hris/kpi/${id}`, data);
export const deleteHrisKPI = (id) => API.delete(`/hris/kpi/${id}`);

// Pelatihan & Sertifikasi
export const getHrisPelatihan = (params) => API.get('/hris/pelatihan', { params });
export const getHrisPelatihanById = (id) => API.get(`/hris/pelatihan/${id}`);
export const createHrisPelatihan = (data) => API.post('/hris/pelatihan', data);
export const updateHrisPelatihan = (id, data) => API.put(`/hris/pelatihan/${id}`, data);
export const deleteHrisPelatihan = (id) => API.delete(`/hris/pelatihan/${id}`);
export const uploadHrisPelatihanFile = (file, id, onProgress) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('id', id || '0');
  return API.post('/hris/pelatihan/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    onUploadProgress: onProgress,
  });
};

// Pengunduran Diri
export const getHrisPengunduranDiri = (params) => API.get('/hris/pengunduran-diri', { params });
export const getHrisPengunduranDiriById = (id) => API.get(`/hris/pengunduran-diri/${id}`);
export const createHrisPengunduranDiri = (data) => API.post('/hris/pengunduran-diri', data);
export const updateHrisPengunduranDiri = (id, data) => API.put(`/hris/pengunduran-diri/${id}`, data);
export const deleteHrisPengunduranDiri = (id) => API.delete(`/hris/pengunduran-diri/${id}`);

// ========================================
// Ticketing API
// ========================================
export const getTickets = (params) => API.get('/ticketing', { params });
export const getTicketingVehicles = (params) => API.get('/ticketing/vehicles', { params });
export const getTicketingStats = (params) => API.get('/ticketing/stats', { params });
export const getTicketById = (id) => API.get(`/ticketing/${id}`);
export const createTicket = (data) => API.post('/ticketing', data);
export const updateTicket = (id, data) => API.put(`/ticketing/${id}`, data);
export const deleteTicket = (id) => API.delete(`/ticketing/${id}`);
export const approveTicket = (id, data) => API.put(`/ticketing/${id}/approve`, data);
export const prosesTicket = (id) => API.put(`/ticketing/${id}/proses`);
export const selesaiTicket = (id, data) => API.put(`/ticketing/${id}/selesai`, data);
export const respondTicket = (id, data) => API.put(`/ticketing/${id}/respond`, data);

// HRIS Dashboard
export const getHrisDashboard = () => API.get('/hris/dashboard');

// CRM
export const getCRMContacts = (params = {}) => API.get('/crm/contacts', { params });
export const getCRMContact = (id) => API.get(`/crm/contacts/${id}`);
export const getCRMContactSummary = (id) => API.get(`/crm/contacts/${id}/summary`);
export const createCRMContact = (data) => API.post('/crm/contacts', data);
export const updateCRMContact = (id, data) => API.put(`/crm/contacts/${id}`, data);
export const deleteCRMContact = (id) => API.delete(`/crm/contacts/${id}`);
export const getCRMDeals = (params = {}) => API.get('/crm/deals', { params });
export const getCRMDeal = (id) => API.get(`/crm/deals/${id}`);
export const createCRMDeal = (data) => API.post('/crm/deals', data);
export const updateCRMDeal = (id, data) => API.put(`/crm/deals/${id}`, data);
export const updateCRMDealStage = (id, stage) => API.put(`/crm/deals/${id}/stage`, { stage });
export const deleteCRMDeal = (id, keterangan = '', password = '') => API.post(`/crm/deals/${id}/delete`, { keterangan, password });
export const getCRMPipeline = () => API.get('/crm/pipeline');
export const getCRMRecentActivity = () => API.get('/crm/recent-activity');
export const getCRMFollowups = (params = {}) => API.get('/crm/followups', { params });
export const createCRMFollowup = (data) => API.post('/crm/followups', data);
export const updateCRMFollowup = (id, data) => API.put(`/crm/followups/${id}`, data);
export const completeCRMFollowup = (id) => API.put(`/crm/followups/${id}/complete`);
export const deleteCRMFollowup = (id) => API.delete(`/crm/followups/${id}`);
export const getCRMDashboard = () => API.get('/crm/dashboard');
export const getCRMCommLogs = (params = {}) => API.get('/crm/comm-logs', { params });
export const createCRMCommLog = (data) => API.post('/crm/comm-logs', data);
// CRM Deal Attachments & Notes
export const getCRMDealAttachments = (dealId) => API.get(`/crm/deals/${dealId}/attachments`);
export const uploadCRMDealAttachment = (dealId, formData) => API.post(`/crm/deals/${dealId}/attachments`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
export const deleteCRMDealAttachment = (dealId, attachmentId) => API.delete(`/crm/deals/${dealId}/attachments`, { data: { id: attachmentId } });
export const getCRMDealNotes = (dealId) => API.get(`/crm/deals/${dealId}/notes`);
export const createCRMDealNote = (dealId, data) => API.post(`/crm/deals/${dealId}/notes`, data);
// CRM MOM (Minutes of Meeting)
export const getCRMMomList = (params = {}) => API.get('/crm/mom', { params });
export const getCRMMom = (id) => API.get(`/crm/mom/${id}`);
export const createCRMMom = (data) => API.post('/crm/mom', data);
export const updateCRMMom = (id, data) => API.put(`/crm/mom/${id}`, data);
export const deleteCRMMom = (id) => API.delete(`/crm/mom/${id}`);
export const uploadCRMMomAttachments = (momId, formData) => API.post(`/crm/mom/${momId}/attachments`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
export const deleteCRMMomAttachment = (momId, attachmentId) => API.delete(`/crm/mom/${momId}/attachments`, { data: { id: attachmentId } });

export default API;
